import { createSlice } from '@reduxjs/toolkit';
import { DISABLE_FLOW_DEEP_LINKING_PROTECTION } from 'config';
import { RadioOptionKeys } from 'tools/RadioOptionMapper';
import {
  CentralHotDeliveryEnum,
  CentralHotWaterDelivery,
  HeatingCostsInvoiceEnum,
  HeatingTypeEnum,
  StoreObject
} from './index';

export interface Address {
  street: string;
  houseNumber: string;
  zip: string;
  city: string;
}

const initialState: StoreObject = {
  numberOfApartments: undefined,
  heatingCostsInvoice: '',
  centralHeatingAvailable: '',
  centralHotWaterDelivery: '',
  heatingType: '',
  heatingSources: [],
  contactDetails: {
    gender: '',
    fname: '',
    lname: '',
    phone: '',
    email: ''
  },
  invoiceAddress: {
    street: '',
    houseNumber: '',
    zip: '',
    city: ''
  },
  houseAddress: {
    street: '',
    houseNumber: '',
    zip: '',
    city: ''
  },
  billingPeriod: {
    startDate: '',
    endDate: ''
  },
  givenConsents: [],
  marketingConsent: {
    consentGiven: false,
    consentVersion: ''
  },
  products: [],
  reducedProducts: [],
  selectedProducts: []
};

const initialDevelopState: StoreObject = {
  numberOfApartments: 2,
  heatingCostsInvoice: HeatingCostsInvoiceEnum.None,
  centralHeatingAvailable: 'centralheating_yes',
  centralHotWaterDelivery: CentralHotDeliveryEnum.HotWaterDeliveryYes,
  heatingType: HeatingTypeEnum.Radiator,
  heatingSources: ['long_distance_heating'],
  contactDetails: {
    gender: 'male',
    fname: 'Torsten',
    lname: 'Tester',
    phone: '0124242412',
    email: 'torsten@tester.de'
  },
  invoiceAddress: {
    street: '',
    houseNumber: '',
    zip: '',
    city: ''
  },
  houseAddress: {
    street: '',
    houseNumber: '',
    zip: '',
    city: ''
  },
  billingPeriod: {
    startDate: '',
    endDate: ''
  },
  givenConsents: [],
  marketingConsent: {
    consentGiven: false,
    consentVersion: 'T1A-2a'
  },
  products: [],
  reducedProducts: [],
  selectedProducts: []
};

export const onboardingInformationSlice = createSlice({
  name: 'onboardingInfo',
  initialState: DISABLE_FLOW_DEEP_LINKING_PROTECTION ? initialDevelopState : initialState,
  reducers: {
    resetState: () => {
      return { ...initialState };
    },
    setNumberOfApartments: (state, action) => {
      state.numberOfApartments = action.payload;
    },
    setHeatingCostsInvoice: (state, action) => {
      state.heatingCostsInvoice = action.payload;
    },
    setCentralHeatingAvailable: (state, action) => {
      state.centralHeatingAvailable = action.payload;
    },
    setCentralHotWaterDelivery: (state, action) => {
      state.centralHotWaterDelivery = action.payload;
    },
    setHeatingType: (state, action) => {
      state.heatingType = action.payload;
    },
    setHeatingSources: (state, action) => {
      state.heatingSources = action.payload;
    },
    setContactDetails: (state, action) => {
      state.contactDetails = action.payload;
    },
    setInvoiceAddress: (state, action) => {
      state.invoiceAddress = action.payload;
    },
    setHouseAddress: (state, action) => {
      state.houseAddress = action.payload;
    },
    setBillingPeriod: (state, action) => {
      state.billingPeriod = {
        startDate: action.payload.startDate.replace('.000', ''),
        endDate: action.payload.endDate.replace('.000', '')
      };
    },
    setGivenConsents: (state, action) => {
      state.givenConsents = action.payload;
    },
    setMarketingConsent: (state, action) => {
      state.marketingConsent = action.payload;
    },
    setProducts: (state, action) => {
      state.products = action.payload;
    },
    setReducedProducts: (state, action) => {
      state.reducedProducts = action.payload;
    },
    setSelectedProducts: (state, action) => {
      state.selectedProducts = action.payload;
    },
    addSelectedProductWithName: (state, action) => {
      if (!state.selectedProducts.find((item) => item.name === action.payload)) {
        if (state.reducedProducts.find((item) => item.name === action.payload)) {
          state.selectedProducts = state.selectedProducts.concat([
            state.reducedProducts.find((item) => item.name === action.payload)!
          ]);
        } else if (state.products.find((item) => item.name === action.payload)) {
          state.selectedProducts = state.selectedProducts.concat([
            state.products.find((item) => item.name === action.payload)!
          ]);
        }
      }
    },
    removeSelectedProductWithName: (state, action) => {
      if (state.selectedProducts.find((item) => item.name === action.payload)) {
        state.selectedProducts = state.selectedProducts.filter(
          (item) => item.name !== action.payload
        );
      }
    },
    setAllFromAPIResponse: (state, action) => {
      state.numberOfApartments = action.payload.propertyDetails.numberOfApartments;
      state.heatingCostsInvoice = action.payload.propertyDetails.heatingCostsInvoice;
      state.centralHeatingAvailable = action.payload.propertyDetails.centralHeatingAvailable
        ? RadioOptionKeys.CentralHeatingAvailable.Yes
        : RadioOptionKeys.CentralHeatingAvailable.No;
      state.centralHotWaterDelivery = action.payload.propertyDetails.centralHotWaterDelivery
        ? (RadioOptionKeys.HotWaterDeliveryCentral.Yes as CentralHotWaterDelivery)
        : (RadioOptionKeys.HotWaterDeliveryCentral.No as CentralHotWaterDelivery);
      state.heatingType = action.payload.propertyDetails.heatingType;
      state.heatingSources = action.payload.propertyDetails.heatingSources;
      state.contactDetails = {
        gender: action.payload.contactCustomer.gender,
        fname: action.payload.contactCustomer.firstName,
        lname: action.payload.contactCustomer.lastName,
        phone: action.payload.contactCustomer.phoneNumber,
        email: action.payload.contactCustomer.email
      };
      state.invoiceAddress = {
        street: action.payload.addressInvoice.street,
        houseNumber: action.payload.addressInvoice.houseNr,
        zip: action.payload.addressInvoice.zip,
        city: action.payload.addressInvoice.city
      };
      state.houseAddress = {
        street: action.payload.addressProperty.street,
        houseNumber: action.payload.addressProperty.houseNr,
        zip: action.payload.addressProperty.zip,
        city: action.payload.addressProperty.city
      };
      state.billingPeriod = action.payload.billingPeriod;
      state.givenConsents = action.payload.consentAgreement;
      state.marketingConsent = action.payload.marketingConsent;
      state.products = action.payload.products.map((item: any) => {
        return {
          name: item.name,
          price: '' + item.price,
          currency: item.currency
        };
      });
    },
    setEMailAddress: (state, action) => {
      state.contactDetails.email = action.payload;
    }
  }
});

export const {
  resetState,
  setNumberOfApartments,
  setHeatingCostsInvoice,
  setCentralHeatingAvailable,
  setCentralHotWaterDelivery,
  setHeatingType,
  setHeatingSources,
  setContactDetails,
  setInvoiceAddress,
  setHouseAddress,
  setBillingPeriod,
  setGivenConsents,
  setMarketingConsent,
  setProducts,
  setReducedProducts,
  setSelectedProducts,
  addSelectedProductWithName,
  removeSelectedProductWithName,
  setAllFromAPIResponse,
  setEMailAddress
} = onboardingInformationSlice.actions;

export default onboardingInformationSlice.reducer;

export const selectOnboardingInfo = (state: StoreObject) => state;
